import { EnvironmentLifetimeExceededModal } from "@/components/environments/EnvironmentLifetimeExceededModal";
import { DropdownMenuItem, DropdownMenuSeparator } from "@/components/podkit/dropdown/DropDown";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useOpenEditor } from "@/hooks/use-open-editor";
import type { PlainEditor } from "@/queries/editor-queries";
import { useStartEnvironment, useStopEnvironment, type PlainEnvironment } from "@/queries/environment-queries";
import { useRunner } from "@/queries/runner-queries";
import { DeleteEnvironmentModal, ForceDeleteEnvironmentModal } from "@/routes/environments/DeleteEnvironmentModal";
import { canOpen, environmentHasReachedStablePhase, showStart, showStop } from "@/routes/environments/phase";
import { formatError, MaximumEnvironmentLifetimeExceededError } from "@/utils/errors";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { RunnerProvider } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import { useCallback, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";

export const EnvironmentActionsDropdown: FC<{
    button?: React.ReactNode;
    environment: PlainEnvironment;
    defaultEditor?: PlainEditor;
    handleViewDetails?: () => void;
}> = ({ button, environment, defaultEditor, handleViewDetails }) => {
    const { toast } = useToast();
    const navigate = useNavigate();

    const startEnvironment = useStartEnvironment();
    const stopEnvironment = useStopEnvironment();
    const { data: runner } = useRunner(environment.metadata?.runnerId);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showForceDeleteModal, setShowForceDeleteModal] = useState(false);
    const [showLifetimeExceededModal, setShowLifetimeExceededModal] = useState(false);

    const isOnManagedRunner = runner?.provider === RunnerProvider.MANAGED;

    const handleViewLogs = useCallback(() => {
        navigate(`/details/${environment.id}/logs`);
    }, [environment.id, navigate]);

    const handleViewTaskRuns = useCallback(() => {
        navigate(`/details/${environment.id}/tasks`);
    }, [environment.id, navigate]);

    const handleStart = useCallback(async () => {
        try {
            await startEnvironment.mutateAsync({ environmentId: environment.id });
        } catch (error) {
            if (error instanceof MaximumEnvironmentLifetimeExceededError) {
                setShowLifetimeExceededModal(true);
                return;
            }
            toast({
                title: "Failed to start environment",
                description: formatError(error),
            });
        }
    }, [startEnvironment, environment.id, toast]);

    const handleCopyId = useCallback(async () => {
        await navigator.clipboard.writeText(environment.id);
        toast({
            title: `Environment ID copied to clipboard`,
            description: environment.id,
        });
    }, [toast, environment.id]);

    const openEditor = useOpenEditor();
    const handleStop = useCallback(() => {
        stopEnvironment.mutate(environment.id, {
            onError: (e) => {
                toast({
                    title: "Failed to stop environment",
                    description: formatError(e),
                });
            },
        });
    }, [stopEnvironment, toast, environment.id]);
    const handleDelete = useCallback(() => setShowDeleteModal(true), [setShowDeleteModal]);
    const handleForceDelete = useCallback(() => setShowForceDeleteModal(true), [setShowForceDeleteModal]);

    return (
        <>
            <DropdownActions button={button} triggerTestId="environment-actions-dropdown-trigger">
                <DropdownMenuItem
                    onClick={() => defaultEditor && openEditor(defaultEditor, environment)}
                    disabled={!canOpen(environment) || !defaultEditor}
                    data-tracking-id="open-environment-environment-actions-dropdown"
                >
                    Open
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={handleViewLogs}
                    data-testid="environment-actions-dropdown-view-logs"
                    data-tracking-id="view-environment-logs-environment-actions-dropdown"
                >
                    View Logs
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={handleViewTaskRuns}
                    data-testid="environment-actions-dropdown-view-task-runs"
                    data-tracking-id="view-environment-task-runs-environment-actions-dropdown"
                >
                    View Task Runs
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {handleViewDetails && (
                    <DropdownMenuItem
                        onClick={handleViewDetails}
                        data-tracking-id="view-environment-details-environment-actions-dropdown"
                    >
                        View Details
                    </DropdownMenuItem>
                )}
                <DropdownMenuItem
                    onClick={handleCopyId}
                    data-tracking-id="copy-environment-id-environment-actions-dropdown"
                >
                    Copy ID
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                    onClick={handleStart}
                    disabled={!showStart(environment) || !environmentHasReachedStablePhase(environment)}
                    data-testid="environment-actions-dropdown-start"
                    data-tracking-id="start-environment-environment-actions-dropdown"
                >
                    Start
                </DropdownMenuItem>
                <DropdownMenuItem
                    onClick={handleStop}
                    disabled={!showStop(environment)}
                    data-testid="environment-actions-dropdown-stop"
                    data-tracking-id="stop-environment-environment-actions-dropdown"
                >
                    Stop
                </DropdownMenuItem>
                {/* Show Delete, when the Environment has not yet been requested to delete */}
                {environment.spec?.desiredPhase !== EnvironmentPhase.DELETED && (
                    <DropdownMenuItem
                        className="text-red-500 focus:text-red-500"
                        onClick={handleDelete}
                        data-tracking-id="delete-environment-environment-actions-dropdown"
                    >
                        Delete
                    </DropdownMenuItem>
                )}
                {/* Show Force Delete, when the Environment has been requested to delete and is not on a managed runner */}
                {environment.spec?.desiredPhase === EnvironmentPhase.DELETED && !isOnManagedRunner && (
                    <DropdownMenuItem
                        className="text-red-500 focus:text-red-500"
                        onClick={handleForceDelete}
                        data-tracking-id="force-delete-environment-environment-actions-dropdown"
                    >
                        Force Delete
                    </DropdownMenuItem>
                )}
            </DropdownActions>
            <DeleteEnvironmentModal
                open={showDeleteModal}
                environment={environment}
                onClose={() => setShowDeleteModal(false)}
            />
            <ForceDeleteEnvironmentModal
                open={showForceDeleteModal}
                environment={environment}
                onClose={() => setShowForceDeleteModal(false)}
            />
            <EnvironmentLifetimeExceededModal
                environment={environment}
                open={showLifetimeExceededModal}
                onClose={() => setShowLifetimeExceededModal(false)}
            />
        </>
    );
};
